import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";

const QuickScan = ({ isQuickScanOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState("scan");
    const [cameraAllowed, setCameraAllowed] = useState(false);
    const [code, setCode] = useState("");
    const navigate = useNavigate();
    const scannerRef = useRef(null);
    const qrRegionId = "qr-reader";
    const isScanningRef = useRef(false);
    const streamRef = useRef(null); // Track camera stream

    // Start camera when scan tab becomes active
    useEffect(() => {
        if (activeTab === "scan" && isQuickScanOpen) {
            console.log("Starting scanner..."); // Debug log
            startScanner();
        } else {
            stopScanner();
        }

        // Cleanup when component unmounts
        return () => {
            stopScanner();
        };
    }, [activeTab, isQuickScanOpen]);

    const startScanner = async () => {
        // Don't start if already scanning
        if (isScanningRef.current) return;

        try {
            // Request camera permission
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "environment" }
            });

            streamRef.current = stream;
            setCameraAllowed(true);

            // Wait for DOM to render
            setTimeout(() => {
                // Only proceed if we're still on scan tab and not already scanning
                if (activeTab === "scan" && !isScanningRef.current) {
                    const html5QrCode = new Html5Qrcode(qrRegionId);
                    scannerRef.current = html5QrCode;
                    isScanningRef.current = true;

                    html5QrCode.start(
                        { facingMode: "environment" },
                        {
                            fps: 10,
                            qrbox: 250,
                        },
                        (decodedText) => {
                            stopScanner();
                            window.location.href = decodedText;
                        },
                        () => { }
                    ).catch((err) => {
                        console.log("Scanner error:", err);
                        setCameraAllowed(false);
                        isScanningRef.current = false;
                    });
                } else {
                    // Clean up stream if not starting scanner
                    if (streamRef.current) {
                        streamRef.current.getTracks().forEach(track => track.stop());
                        streamRef.current = null;
                    }
                }
            }, 300);

        } catch (err) {
            console.log("Camera permission denied:", err);
            setCameraAllowed(false);
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
        }
    };

    const stopScanner = async () => {
        // Reset scanning flag immediately to prevent any new scans
        isScanningRef.current = false;

        try {
            // Stop the scanner if it exists
            if (scannerRef.current) {
                try {
                    console.log("Stopping scanner..."); // Debug log
                    await scannerRef.current.stop();
                } catch (e) {
                    console.log("Stop error:", e);
                }

                try {
                    console.log("Clearing scanner..."); // Debug log
                    await scannerRef.current.clear();
                } catch (e) {
                    console.log("Clear error:", e);
                }

            }

            // Stop all camera tracks
            if (streamRef.current) {
                console.log("Stopping camera tracks..."); // Debug log
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }

        } catch (err) {
            console.log("Stop error:", err);
        }

        setCameraAllowed(false);
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopScanner();
        };
    }, []);

    const handleClose = async () => {
        await stopScanner(); // Ensure camera stops before closing
        window.location.reload(); // Force reload to reset camera state
        if (onClose) onClose();
    };

    const handleTabChange = async (tab) => {
        if (tab === "code") {
            await stopScanner();
        }
        setActiveTab(tab);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
            {/* overlay */}
            <div className="absolute inset-0 bg-black/60" onClick={handleClose} />

            {/* modal */}
            <div className="relative w-full md:max-w-[440px] bg-white md:rounded-[20px] rounded-t-[20px] overflow-hidden shadow-[0px_8px_40px_rgba(0,0,0,0.2)] max-h-[90vh] flex flex-col">

                {/* header */}
                <div className="flex items-center justify-between px-[20px] py-[16px] border-b border-[#e4e5e7]">
                    <div className="flex items-center gap-[10px]">
                        <div className="w-[36px] h-[36px] rounded-[10px] bg-[#fff0ec] flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-zap w-[18px] h-[18px] text-[#ff5323]">
                                <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path>
                            </svg>
                        </div>

                        <div>
                            <h2 className="font-semibold text-[17px] text-[#212326]">
                                QuickScan
                            </h2>
                            <p className="font-medium text-[12px] text-[#717182]">
                                Scan QR or enter shop code
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={handleClose}
                        className="w-[32px] h-[32px] rounded-full bg-[#f4f5f6] flex items-center justify-center hover:bg-[#e4e5e7]"
                    >
                        ✕
                    </button>
                </div>

                {/* tabs */}
                <div className="flex border-b border-[#e4e5e7]">
                    <button
                        onClick={() => handleTabChange("scan")}
                        className={`flex-1 flex items-center justify-center gap-[8px] py-[12px] font-semibold text-[14px] transition-colors
                            ${activeTab === "scan"
                                ? "text-[#ff5323] bg-[#fff8f5] border-b-2 border-[#ff5323]"
                                : "text-[#717182] bg-white hover:bg-[#fafafa]"
                            }`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-camera w-[16px] h-[16px]">
                            <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path>
                            <circle cx="12" cy="13" r="3"></circle>
                        </svg>
                        Scan QR
                    </button>

                    <button
                        onClick={() => handleTabChange("code")}
                        className={`flex-1 flex items-center justify-center gap-[8px] py-[12px] font-semibold text-[14px] transition-colors
                            ${activeTab === "code"
                                ? "text-[#ff5323] bg-[#fff8f5] border-b-2 border-[#ff5323]"
                                : "text-[#717182] bg-white hover:bg-[#fafafa]"
                            }`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-keyboard w-[16px] h-[16px]">
                            <path d="M10 8h.01"></path><path d="M12 12h.01"></path><path d="M14 8h.01"></path><path d="M16 12h.01"></path>
                            <path d="M18 8h.01"></path><path d="M6 8h.01"></path><path d="M7 16h10"></path><path d="M8 12h.01"></path>
                            <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                        </svg>
                        Enter Code
                    </button>
                </div>

                {/* content */}
                <div className="flex-1 overflow-y-auto p-[20px]">
                    {/* SCAN TAB */}
                    {activeTab === "scan" && (
                        <>
                            {cameraAllowed ? (
                                <div
                                    id={qrRegionId}
                                    className="w-full h-[320px] bg-black rounded-[12px] overflow-hidden"
                                />
                            ) : (
                                <div className="flex flex-col items-center gap-[16px] py-[24px]">
                                    <div className="w-[56px] h-[56px] rounded-full bg-[#fef3f0] flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-alert w-[28px] h-[28px] text-[#ff5323]">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="12" x2="12" y1="8" y2="12"></line>
                                            <line x1="12" x2="12.01" y1="16" y2="16"></line>
                                        </svg>
                                    </div>

                                    <p className="font-medium text-[14px] text-[#717182] text-center leading-[1.5] max-w-[300px]">
                                        Camera access denied. Please allow camera permission in your browser settings, or enter the shop code manually.
                                    </p>

                                    <button
                                        onClick={() => handleTabChange("code")}
                                        className="flex items-center gap-[8px] px-[20px] py-[10px] rounded-[10px] bg-[#ff5323] text-white font-semibold text-[14px] hover:bg-[#e64a1f] transition-colors border-none cursor-pointer"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-keyboard w-[16px] h-[16px]">
                                            <path d="M10 8h.01"></path><path d="M12 12h.01"></path><path d="M14 8h.01"></path><path d="M16 12h.01"></path>
                                            <path d="M18 8h.01"></path><path d="M6 8h.01"></path><path d="M7 16h10"></path><path d="M8 12h.01"></path>
                                            <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                                        </svg>
                                        Enter Code Instead
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {/* CODE TAB */}
                    {activeTab === "code" && (
                        <div className="flex-1 overflow-y-auto">
                            <div className="p-[16px] flex flex-col gap-[16px]">
                                {/* ICON + TEXT */}
                                <div className="flex flex-col items-center gap-[8px] py-[8px]">
                                    <div className="w-[56px] h-[56px] rounded-[14px] bg-[#f4f5f6] flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                            className="w-[26px] h-[26px] text-[#53585f]">
                                            <path d="M3 7V5a2 2 0 0 1 2-2h2"></path>
                                            <path d="M17 3h2a2 2 0 0 1 2 2v2"></path>
                                            <path d="M21 17v2a2 2 0 0 1-2 2h-2"></path>
                                            <path d="M7 21H5a2 2 0 0 1-2-2v-2"></path>
                                            <path d="M7 12h10"></path>
                                        </svg>
                                    </div>

                                    <p className="font-medium text-[13px] text-[#717182] text-center leading-[1.5] max-w-[280px]">
                                        Enter the 6-character shop code displayed at the print shop counter
                                    </p>
                                </div>

                                {/* INPUT + BUTTON */}
                                <div className="flex flex-col gap-[10px]">
                                    <input
                                        type="text"
                                        maxLength={6}
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                                        placeholder="e.g. JET001"
                                        className="w-full h-[48px] rounded-[14px] border-2 border-[#e4e5e7] px-[20px] font-semibold text-[22px] text-center text-[#212326] tracking-[6px] placeholder:text-[#c0c2c6] placeholder:tracking-[4px] placeholder:font-medium placeholder:text-[16px] focus:outline-none focus:border-[#ff5323] uppercase"
                                    />

                                    <button
                                        onClick={() => navigate(`/marketplace/upload/?shopCode=${code}`)}
                                        disabled={code.length < 6}
                                        className={`w-full flex items-center justify-center gap-[8px] py-[12px] rounded-[12px] font-semibold text-[15px] transition-all
                                        ${code.length < 6
                                                ? "bg-[#e4e5e7] text-[#9298a0] cursor-not-allowed"
                                                : "bg-[#ff5323] text-white hover:bg-[#e64a1f]"
                                            }`}
                                    >
                                        Go to Upload
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                            className="w-[16px] h-[16px]">
                                            <path d="M5 12h14"></path>
                                            <path d="m12 5 7 7-7 7"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuickScan;